import type { Config, FigureId } from '../core/config/defaults';
import type { DrawResult, PrizeService } from '../core/contracts';

const FIGURE_IDS: readonly FigureId[] = ['A', 'B', 'C'];

/**
 * Stand-in for the future `/api/draw`.
 *
 * Shaped like a network call on purpose — latency, abort support and a rejection path — because
 * the point of the seam is that swapping in a real backend changes nothing else. A mock that
 * always resolves instantly would let the caller's error handling rot untested, which is why the
 * debug panel can force a failure.
 */
export class MockPrizeService implements PrizeService {
  /** Set by the debug panel to exercise the failure path. */
  forceFailure = false;

  constructor(private readonly cfg: Config) {}

  async draw(_userId: string, signal?: AbortSignal): Promise<DrawResult> {
    const [min, max] = this.cfg.TIMING.prizeLatencyMs;
    const delay = min + Math.random() * (max - min);

    await new Promise<void>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      const timer = window.setTimeout(resolve, delay);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new DOMException('aborted', 'AbortError'));
        },
        { once: true },
      );
    });

    if (this.forceFailure) throw new Error('draw failed (forced by debug panel)');

    const figureId =
      this.cfg.PRIZE.mode === 'random'
        ? (FIGURE_IDS[Math.floor(Math.random() * FIGURE_IDS.length)] ?? 'A')
        : this.cfg.PRIZE.fixedId;

    return {
      status: 'win',
      figureId,
      couponUrl: this.cfg.ASSET.coupon,
      couponCode: 'DEMO-0000-0000-0000',
      // ISO-8601 UTC in transit. Displayed in KST — never store a naive local time.
      issuedAt: new Date().toISOString(),
    };
  }
}
